import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { PRODUCTS_DEPARTMENTS, PRODUCTS_BRANDS } from '../../data/productsCatalog';

// Bespoke 3D illustrated department icons (matching the homepage pillar /
// toolbox-icon style), keyed by department.
export const DEPT_ICONS = {
  'furniture': '/images/dept-icons/furniture.webp',
  'lighting-decor': '/images/dept-icons/lighting.webp',
  'kitchen-dining': '/images/dept-icons/kitchen.webp',
  'bedroom-sleep': '/images/dept-icons/bedroom.webp',
  'outdoor-garden': '/images/dept-icons/outdoor.webp',
  'cleaning-tools': '/images/dept-icons/cleaning.webp',
  'bathroom-reno': '/images/dept-icons/bathroom.webp',
  'energy-power': '/images/dept-icons/energy.webp',
  'window-furnishings': '/images/dept-icons/window-furnishings.webp',
  'smart-home': '/images/dept-icons/smart-home.webp',
  'internet-nbn': '/images/dept-icons/internet.webp',
};

// Per-department featured lifestyle image shown in the middle pane so the
// panel never leaves empty space. Appended CDN params keep them light.
const IMG = (url) => `${url}&w=480&h=320&fit=crop`;
export const FEATURED_IMAGES = {
  'furniture': IMG('https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&q=70&ixlib=rb-4.1.0'),
  'lighting-decor': IMG('https://images.unsplash.com/photo-1608626597747-0d9b8884972c?crop=entropy&cs=srgb&fm=jpg&q=70&ixlib=rb-4.1.0'),
  'kitchen-dining': IMG('https://images.unsplash.com/photo-1656402887556-e727ffe1f6d7?crop=entropy&cs=srgb&fm=jpg&q=70&ixlib=rb-4.1.0'),
  'bedroom-sleep': IMG('https://images.unsplash.com/photo-1616418928117-4e6d19be2df1?crop=entropy&cs=srgb&fm=jpg&q=70&ixlib=rb-4.1.0'),
  'outdoor-garden': IMG('https://images.unsplash.com/photo-1621506821957-1b50ab7787a4?crop=entropy&cs=srgb&fm=jpg&q=70&ixlib=rb-4.1.0'),
  'cleaning-tools': IMG('https://images.unsplash.com/photo-1653990480360-31a12ce9723e?crop=entropy&cs=srgb&fm=jpg&q=70&ixlib=rb-4.1.0'),
  'bathroom-reno': IMG('https://images.unsplash.com/photo-1631048499052-e6d9f305d2c0?crop=entropy&cs=srgb&fm=jpg&q=70&ixlib=rb-4.1.0'),
  'energy-power': IMG('https://images.unsplash.com/photo-1589276534126-adef63a95e05?crop=entropy&cs=srgb&fm=jpg&q=70&ixlib=rb-4.1.0'),
  'window-furnishings': IMG('https://images.unsplash.com/photo-1577955107081-92c3197fb6cb?crop=entropy&cs=srgb&fm=jpg&q=70&ixlib=rb-4.1.0'),
  'smart-home': IMG('https://images.unsplash.com/photo-1549109926-58f039549485?crop=entropy&cs=srgb&fm=jpg&q=70&ixlib=rb-4.1.0'),
  'internet-nbn': IMG('https://images.unsplash.com/photo-1733810763720-4c83af0668ea?crop=entropy&cs=srgb&fm=jpg&q=70&ixlib=rb-4.1.0'),
};

/**
 * ProductsMegaMenu — compact retail flyout rendered inside the shared glass
 * container. Three tight panes (3 / 5 / 4) sized so the tallest column stays
 * short, with the middle anchoring its buying guides to the base so sparse
 * departments no longer leave an empty void.
 *   Left  : departments (hover to reveal subcategories).
 *   Middle: active department's subcategories + buying guides.
 *   Right : a two-column brand grid + a slim "Shop all products" tile.
 */
const ProductsMegaMenu = ({ onNavigate }) => {
  const [activeKey, setActiveKey] = useState(PRODUCTS_DEPARTMENTS[0].key);
  const active = PRODUCTS_DEPARTMENTS.find((d) => d.key === activeKey) || PRODUCTS_DEPARTMENTS[0];
  const guides = (active.guides || []).filter((g) => g.href.startsWith('/learn')).slice(0, 4);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12" data-testid="mega-menu-products-panel">
      {/* LEFT — department list */}
      <nav className="lg:col-span-3 px-3 py-3.5 border-r border-black/5" aria-label="Product departments">
        <h3 className="bp-megamenu-glass__eyebrow text-[10.5px] font-semibold uppercase tracking-[0.06em] px-2 mb-1.5">
          Shop by department
        </h3>
        <ul>
          {PRODUCTS_DEPARTMENTS.map((d) => {
            const isActive = d.key === activeKey;
            return (
              <li key={d.key}>
                <Link
                  to={`/products/${d.key}`}
                  onClick={onNavigate}
                  onMouseEnter={() => setActiveKey(d.key)}
                  onFocus={() => setActiveKey(d.key)}
                  className={`group flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors ${isActive ? 'bg-[#1d8efe]/10' : 'hover:bg-black/5'}`}
                  data-testid={`mega-products-dept-${d.key}`}
                >
                  <span className="w-7 h-7 rounded-md bg-white flex items-center justify-center shrink-0 overflow-hidden ring-1 ring-black/5">
                    <img
                      src={DEPT_ICONS[d.key]}
                      alt=""
                      aria-hidden="true"
                      loading="eager"
                      decoding="async"
                      className="w-5 h-5 object-contain"
                    />
                  </span>
                  <span className={`flex-1 text-[13px] font-semibold leading-tight ${isActive ? 'text-[#1d8efe]' : 'bp-megamenu-glass__link-title'}`}>
                    {d.label}
                  </span>
                  <ChevronRight className={`w-3.5 h-3.5 transition-opacity ${isActive ? 'text-[#1d8efe] opacity-100' : 'bp-megamenu-glass__link-desc opacity-40'}`} />
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* MIDDLE — active department's subcategories (guides anchored to base) */}
      <div className="lg:col-span-5 px-5 xl:px-6 py-4 flex flex-col" data-testid="mega-products-subcats">
        <div className="flex items-center justify-between mb-2.5">
          <Link
            to={`/products/${active.key}`}
            onClick={onNavigate}
            className="bp-megamenu-glass__link-title text-[14px] font-bold hover:text-[#1d8efe] transition-colors"
          >
            {active.label}
          </Link>
          <Link
            to={`/products/${active.key}`}
            onClick={onNavigate}
            className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#1d8efe]"
            data-testid={`mega-products-viewall-${active.key}`}
          >
            Shop all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
          {active.subcategories.map((sc) => (
            <Link
              key={sc.cat + sc.label}
              to={`/products/${active.key}?cat=${sc.cat}`}
              onClick={onNavigate}
              className="group/sub flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 hover:bg-white/[0.06] transition-colors"
              data-testid={`mega-products-subcat-${sc.cat}`}
            >
              <span className="bp-megamenu-glass__link-desc text-[13px] font-medium group-hover/sub:text-[#1d8efe] transition-colors truncate">{sc.label}</span>
              <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[#1d8efe] opacity-0 -translate-x-1 group-hover/sub:opacity-100 group-hover/sub:translate-x-0 transition-all" />
            </Link>
          ))}
        </div>
        {/* editorial nudge for this department */}
        {guides.length > 0 && (
          <div className="mt-3.5 pt-3 border-t border-black/5">
            <span className="bp-megamenu-glass__link-desc text-[10.5px] uppercase tracking-[0.05em] font-semibold text-white/50">Buying guides</span>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {guides.map((g) => (
                <Link
                  key={g.href}
                  to={g.href}
                  onClick={onNavigate}
                  className="rounded-full border border-white/12 px-2.5 py-1 text-[11.5px] bp-megamenu-glass__link-desc hover:border-[#1d8efe]/50 hover:text-[#1d8efe] transition-colors"
                >
                  {g.name}
                </Link>
              ))}
            </div>
          </div>
        )}
        {/* featured department visual — all images stay mounted and simply
            cross-fade, so hovering a department never triggers a fresh fetch
            or decode (no split-second flash). */}
        {FEATURED_IMAGES[active.key] && (
          <Link
            to={`/products/${active.key}`}
            onClick={onNavigate}
            className="group/feat relative mt-4 flex-1 min-h-[104px] rounded-xl overflow-hidden flex items-end bg-[#1d1d1f]"
            data-testid={`mega-products-featured-${active.key}`}
          >
            {Object.entries(FEATURED_IMAGES).map(([key, src]) => {
              const on = key === active.key;
              return (
                <img
                  key={key}
                  src={src}
                  alt=""
                  aria-hidden="true"
                  decoding="async"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${on ? 'opacity-85 group-hover/feat:opacity-100' : 'opacity-0'}`}
                />
              );
            })}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
            <span className="relative p-3 text-white text-[13px] font-semibold inline-flex items-center gap-1.5">
              Shop {active.label}
              <ArrowRight className="w-4 h-4 group-hover/feat:translate-x-0.5 transition-transform" />
            </span>
          </Link>
        )}
      </div>

      {/* RIGHT — two-column brand grid + slim visual tile */}
      <div className="lg:col-span-4 bp-megamenu-glass__hero-band px-5 py-4 flex flex-col">
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="bp-megamenu-glass__eyebrow text-[10.5px] font-semibold uppercase tracking-[0.06em]">
            Shop by brand
          </h3>
          <Link
            to="/products"
            onClick={onNavigate}
            className="text-[11px] font-semibold text-[#1d8efe] inline-flex items-center gap-0.5"
            data-testid="mega-products-all-brands"
          >
            View all <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
          {PRODUCTS_BRANDS.map((b) => (
            <li key={b.key}>
              <Link
                to={b.href}
                onClick={onNavigate}
                className="group flex items-center gap-1 py-0.5"
                data-testid={`mega-products-brand-${b.key}`}
              >
                <span className="bp-megamenu-glass__link-title text-[12.5px] font-semibold group-hover:text-[#1d8efe] transition-colors leading-tight truncate">
                  {b.name}
                </span>
                <ArrowRight className="w-3 h-3 bp-megamenu-glass__link-desc opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0" />
              </Link>
            </li>
          ))}
        </ul>

        <Link
          to="/products"
          onClick={onNavigate}
          className="group relative mt-4 rounded-xl overflow-hidden h-[64px] flex items-center bg-[#1d1d1f]"
          data-testid="mega-products-shop-all"
        >
          <img
            src="https://images.unsplash.com/photo-1628744876497-eb30460be9f6?crop=entropy&cs=srgb&fm=jpg&q=80&w=700&ixlib=rb-4.1.0"
            alt="Shop all products"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:scale-105 group-hover:opacity-100 transition-all duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-black/10" />
          <span className="relative px-4 text-white text-[13px] font-semibold inline-flex items-center gap-1.5">
            Shop all products
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </span>
        </Link>
      </div>
    </div>
  );
};

export default ProductsMegaMenu;
